import type { LearnMoreModalData } from './DiagnosticLearnMoreModal';
import { getBuyPrioritizedModalData } from './modalDataFromBuy';

export type ComparisonUsualLabel = {
  labelKey: string;
  labelDefault: string;
};

/** `true` = available, `false` = unavailable, label object = custom text. */
export type ComparisonUsualStatus = boolean | ComparisonUsualLabel;

export interface ComparisonRow {
  id: string;
  titleKey: string;
  titleDefault: string;
  usualStatus: ComparisonUsualStatus;
  /** Custom text for the Performance Lab column; defaults to checkmark when absent. */
  labStatus?: ComparisonUsualLabel;
}

export type ComparisonPillRowConfig = {
  id: string;
  direction: 'left' | 'right';
  /** Embla auto-scroll speed — pixels per animation frame. */
  autoScrollSpeed: number;
  /** Milliseconds before this row begins scrolling. */
  startDelay?: number;
  pillKeys: readonly string[];
  pillDefaults: Record<string, string>;
};

export const COMPARISON_HERO_VIDEO_SRC =
  'web_v2/performance-lab/landing-page/accordions/diagnostics/diagnostic-banner.mp4';

export const COMPARISON_PILL_ROWS: readonly ComparisonPillRowConfig[] = [
  {
    id: 'row-1',
    direction: 'right',
    autoScrollSpeed: 0.55,
    pillKeys: [
      'lungCapacity',
      'jointMobility',
      'cognitiveLoad',
      'inflammationMarkers',
      'sleepArchitecture',
    ],
    pillDefaults: {
      lungCapacity: 'Lung capacity',
      jointMobility: 'Joint mobility',
      cognitiveLoad: 'Cognitive load',
      inflammationMarkers: 'Inflammation markers',
      sleepArchitecture: 'Sleep architecture',
    },
  },
  {
    id: 'row-2',
    direction: 'left',
    autoScrollSpeed: 0.55,
    startDelay: 200,
    pillKeys: [
      'injuryRisk',
      'sleepArchitecture',
      'pharmacogenomics',
      'metabolismType',
      'vo2Max',
    ],
    pillDefaults: {
      injuryRisk: 'Injury risk',
      sleepArchitecture: 'Sleep architecture',
      pharmacogenomics: 'Pharmacogenomics',
      metabolismType: 'Metabolism type',
      vo2Max: 'VO2 Max',
    },
  },
  {
    id: 'row-3',
    direction: 'right',
    autoScrollSpeed: 0.75,
    startDelay: 400,
    pillKeys: [
      'muscleSymmetry',
      'hrv',
      'forceProduction',
      'metabolismType',
      'vo2Max',
    ],
    pillDefaults: {
      muscleSymmetry: 'Muscle symmetry',
      hrv: 'HRV',
      forceProduction: 'Force production',
      metabolismType: 'Metabolism type',
      vo2Max: 'VO2 Max',
    },
  },
];

const mergePillRows = (
  id: string,
  direction: ComparisonPillRowConfig['direction'],
  autoScrollSpeed: number,
  startDelay: number,
  ...rows: ComparisonPillRowConfig[]
): ComparisonPillRowConfig => {
  const seen = new Set<string>();
  const pillKeys: string[] = [];
  const pillDefaults: Record<string, string> = {};

  for (const row of rows) {
    for (const key of row.pillKeys) {
      if (seen.has(key)) continue;
      seen.add(key);
      pillKeys.push(key);
      pillDefaults[key] = row.pillDefaults[key];
    }
  }

  return { id, direction, autoScrollSpeed, startDelay, pillKeys, pillDefaults };
};

/** Two-row pill marquee for mobile — merges desktop rows 2 and 3. */
export const COMPARISON_PILL_ROWS_MOBILE: readonly ComparisonPillRowConfig[] = [
  COMPARISON_PILL_ROWS[0],
  mergePillRows('row-mobile-2', 'left', 0.55, 200, COMPARISON_PILL_ROWS[1], COMPARISON_PILL_ROWS[2]),
];

export const COMPARISON_ROWS: readonly ComparisonRow[] = [
  {
    id: 'biointelligence-ai',
    titleKey: 'comparisonSection.rows.biointelligenceAi',
    titleDefault: 'Biointelligence AI',
    usualStatus: false,
  },
  {
    id: 'longevity-doctor-consultation',
    titleKey: 'comparisonSection.rows.longevityDoctorConsultation',
    titleDefault: 'Longevity Doctor Consultation',
    usualStatus: {
      labelKey: 'comparisonSection.usual.generalistOnly',
      labelDefault: 'Generalist only',
    },
  },
  {
    id: 'nutrition',
    titleKey: 'comparisonSection.rows.nutritionConsultation',
    titleDefault: '1-1 Nutrition Consultation',
    usualStatus: false,
  },
  {
    id: 'system-wise-performance-score',
    titleKey: 'comparisonSection.rows.systemWisePerformanceScore',
    titleDefault: 'System-wise Performance Score',
    usualStatus: false,
  },
  {
    id: 'action-plan',
    titleKey: 'comparisonSection.rows.actionPlan',
    titleDefault: 'Action plan',
    usualStatus: false,
  },
  {
    id: 'blood-vision',
    titleKey: 'comparisonSection.rows.bloodVision',
    titleDefault: 'Blood Vision',
    usualStatus: false,
  },
  {
    id: 'body-composition-analysis',
    titleKey: 'comparisonSection.rows.bodyCompositionDexa',
    titleDefault: 'Body Composition (DEXA)',
    usualStatus: false,
  },
  {
    id: 'bone-density',
    titleKey: 'comparisonSection.rows.boneHealth',
    titleDefault: 'Bone Health (T-score, Z-score)',
    usualStatus: false,
  },
  {
    id: 'electrocardiogram',
    titleKey: 'comparisonSection.rows.cardiovascularEcg',
    titleDefault: 'Cardiovascular (ECG)',
    labStatus: {
      labelKey: 'comparisonSection.lab.advanced',
      labelDefault: 'Advanced',
    },
    usualStatus: {
      labelKey: 'comparisonSection.usual.ecgOnly',
      labelDefault: 'ECG only',
    },
  },
  {
    id: 'vo2-max',
    titleKey: 'comparisonSection.rows.aerobicCapacityVo2Max',
    titleDefault: 'Aerobic Capacity (VO2 max)',
    usualStatus: false,
  },
  {
    id: 'lung-health',
    titleKey: 'comparisonSection.rows.pulmonaryFunction',
    titleDefault: 'Pulmonary Function (Spirometry)',
    usualStatus: false,
  },
  {
    id: 'musculoskeletal-assessment',
    titleKey: 'comparisonSection.rows.musculoskeletalAssessment',
    titleDefault: 'Musculoskeletal Assessment',
    usualStatus: false,
  },
  {
    id: 'cognition',
    titleKey: 'comparisonSection.rows.cognitiveAssessment',
    titleDefault: 'Cognitive Assessment',
    usualStatus: false,
  },
  {
    id: 'vision',
    titleKey: 'comparisonSection.rows.visionOct',
    titleDefault: 'Vision (OCT)',
    usualStatus: false,
  },
  {
    id: 'hearing',
    titleKey: 'comparisonSection.rows.hearingAudiometry',
    titleDefault: 'Hearing (Audiometry)',
    usualStatus: false,
  },
  {
    id: 'oral-health',
    titleKey: 'comparisonSection.rows.oralHealth3dScan',
    titleDefault: 'Oral Health (3D Scan)',
    usualStatus: {
      labelKey: 'comparisonSection.usual.basic',
      labelDefault: 'Basic',
    },
  },
  {
    id: 'gut-microbiome',
    titleKey: 'comparisonSection.rows.gutMicrobiome',
    titleDefault: 'Gut Microbiome',
    usualStatus: false,
  },
  {
    id: 'genomic-health-insights',
    titleKey: 'comparisonSection.rows.geneticTesting',
    titleDefault: 'Genetic Testing',
    usualStatus: false,
  },
  {
    id: 'vitals',
    titleKey: 'comparisonSection.rows.bloodPressure',
    titleDefault: 'Blood Pressure',
    usualStatus: {
      labelKey: 'comparisonSection.usual.basic',
      labelDefault: 'Basic',
    },
  },
];

export const getComparisonRowModalData = (
  rowId: string,
): LearnMoreModalData | undefined => getBuyPrioritizedModalData(rowId);

export const getComparisonRowThumbnailSrc = (
  rowId: string,
): string | undefined => getComparisonRowModalData(rowId)?.thumbnailSrc;
